/**
 * Probe whether `window.localStorage` is usable.
 *
 * iOS in-app browsers (notably Google app WebViews) can expose a null
 * `localStorage`, and Firebase Auth's BrowserLocalPersistence poller then
 * throws `TypeError: null is not an object (evaluating 'this.storage.getItem')`
 * on every tick.
 *
 * LMS iframes and some WebViews throw SecurityError on any read of
 * `window.localStorage` (Sentry JAVASCRIPT-NEXTJS-109 / JAVASCRIPT-NEXTJS-VE).
 */
export function isBrowserLocalStorageAvailable(): boolean {
    if (typeof window === "undefined") return false;
    try {
        const storage = window.localStorage;
        if (!storage) return false;
        const probeKey = "__braiv_ls_probe__";
        storage.setItem(probeKey, "1");
        storage.removeItem(probeKey);
        return true;
    } catch (error) {
        // Quota-full storage is still the real Auth/workspace store; getItem works.
        return isQuotaExceededStorageError(error);
    }
}

const MEMORY_STORAGE_FLAG = Symbol.for("braiv.memoryWebStorage");

function createMemoryStorage(): Storage {
    const store = new Map<string, string>();
    const storage: Storage = {
        get length() {
            return store.size;
        },
        clear() {
            store.clear();
        },
        getItem(key: string) {
            return store.has(key) ? store.get(key) ?? null : null;
        },
        key(index: number) {
            return Array.from(store.keys())[index] ?? null;
        },
        removeItem(key: string) {
            store.delete(key);
        },
        setItem(key: string, value: string) {
            store.set(String(key), String(value));
        },
    };
    Object.defineProperty(storage, MEMORY_STORAGE_FLAG, {value: true});
    return storage;
}

/** True when this storage object is the in-tab shim, which dies on navigation. */
export function isInMemoryWebStorage(storage: unknown): boolean {
    return Boolean(
        storage
        && typeof storage === "object"
        && (storage as Record<symbol, unknown>)[MEMORY_STORAGE_FLAG] === true,
    );
}

export function isQuotaExceededStorageError(error: unknown): boolean {
    const name = String((error as {name?: unknown})?.name || "");
    const message = String((error as {message?: unknown})?.message || error || "").toLowerCase();
    return name === "QuotaExceededError" || name === "NS_ERROR_DOM_QUOTA_REACHED" || message.includes("quota");
}

function storageNeedsShim(target: Pick<Window, "localStorage" | "sessionStorage">, name: "localStorage" | "sessionStorage"): boolean {
    try {
        const storage = target[name];
        if (!storage) return true;
        const probeKey = "__braiv_ls_probe__";
        storage.setItem(probeKey, "1");
        storage.removeItem(probeKey);
        return false;
    } catch (error) {
        // Full-but-usable storage must not be replaced with an empty memory map
        // or Firebase Auth / workspace caches disappear for the tab.
        if (isQuotaExceededStorageError(error)) {
            return false;
        }
        return true;
    }
}

/**
 * Replace inaccessible Web Storage with an in-memory shim so third-party
 * code (Vidstack player prefs, Firebase) can call getItem without crashing
 * embeds and restricted WebViews.
 */
export function installSafeWebStorage(
    target: Pick<Window, "localStorage" | "sessionStorage"> = typeof window === "undefined" ? undefined as never : window,
): boolean {
    if (typeof target === "undefined" || !target) return false;
    let installed = false;
    for (const name of ["localStorage", "sessionStorage"] as const) {
        if (!storageNeedsShim(target, name)) continue;
        try {
            Object.defineProperty(target, name, {
                configurable: true,
                enumerable: true,
                value: createMemoryStorage(),
            });
            installed = true;
        } catch {
            // Some browsers freeze storage accessors; leave them alone.
        }
    }
    return installed;
}
